import { Injectable, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, filter } from 'rxjs';
import { BreadcrumbItem, BreadcrumbAction } from '../models/breadcrumb.model';

@Injectable({
  providedIn: 'root',
})
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly breadcrumbsSubject = new BehaviorSubject<BreadcrumbItem[]>([]);
  readonly breadcrumbs$ = this.breadcrumbsSubject.asObservable();

  private readonly actionsSubject = new BehaviorSubject<BreadcrumbAction[]>([]);
  readonly actions$ = this.actionsSubject.asObservable();

  private readonly showBackButtonSubject = new BehaviorSubject<boolean>(true);
  readonly showBackButton$ = this.showBackButtonSubject.asObservable();

  private readonly backUrlSubject = new BehaviorSubject<string | undefined>(undefined);
  readonly backUrl$ = this.backUrlSubject.asObservable();

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      const breadcrumbs = this.buildBreadcrumbs(this.activatedRoute.root);
      this.breadcrumbsSubject.next(breadcrumbs);

      // Reset actions on navigation
      this.actionsSubject.next([]);
      this.showBackButtonSubject.next(true);
      this.backUrlSubject.next(undefined);
    });
  }

  private buildBreadcrumbs(
    route: ActivatedRoute,
    url: string = '',
    breadcrumbs: BreadcrumbItem[] = []
  ): BreadcrumbItem[] {
    const children = route.children;

    if (children.length === 0) {
      return breadcrumbs;
    }

    for (const child of children) {
      const routeURL: string = child.snapshot.url.map((segment) => segment.path).join('/');

      if (routeURL !== '') {
        url += `/${routeURL}`;
      }

      const breadcrumbData = child.snapshot.data['breadcrumb'];

      if (breadcrumbData) {
        const item: BreadcrumbItem = {
          label: typeof breadcrumbData === 'string' ? breadcrumbData : breadcrumbData.label,
          url: url,
          icon: typeof breadcrumbData === 'object' ? breadcrumbData.icon : undefined,
        };

        const exists = breadcrumbs.find((b) => b.url === url);
        if (!exists) {
          breadcrumbs.push(item);
        }
      }

      return this.buildBreadcrumbs(child, url, breadcrumbs);
    }

    return breadcrumbs;
  }

  setLastBreadcrumbLabel(label: string): void {
    const current = [...this.breadcrumbsSubject.value];
    if (current.length > 0) {
      current[current.length - 1] = {
        ...current[current.length - 1],
        label,
      };
      this.breadcrumbsSubject.next(current);
    }
  }

  updateBreadcrumbLabel(index: number, label: string): void {
    const current = [...this.breadcrumbsSubject.value];
    if (current[index]) {
      current[index] = { ...current[index], label };
      this.breadcrumbsSubject.next(current);
    }
  }

  setBreadcrumbs(items: BreadcrumbItem[]): void {
    this.breadcrumbsSubject.next(items);
  }

  setActions(actions: BreadcrumbAction[]): void {
    this.actionsSubject.next(actions);
  }

  clearActions(): void {
    this.actionsSubject.next([]);
  }

  setShowBackButton(show: boolean): void {
    this.showBackButtonSubject.next(show);
  }

  setBackUrl(url: string | undefined): void {
    this.backUrlSubject.next(url);
  }

  goBack(fallbackUrl?: string): void {
    const backUrl = this.backUrlSubject.value ?? fallbackUrl;

    if (window.history.length > 1) {
      window.history.back();
    } else if (backUrl) {
      this.router.navigate([backUrl]);
    }
  }

  getCurrentBreadcrumbs(): BreadcrumbItem[] {
    return this.breadcrumbsSubject.value;
  }
}
